import type { InstrumentProfile } from '@swarasense/shared';
import { buildDroneTemplate, isVoicePresent } from '@swarasense/shared';
// Vite bundles the worklet (with its imports) and returns its URL — required for
// AudioWorklet.addModule to fetch a self-contained module at runtime.
import pitchProcessorUrl from './pitch-processor.ts?worker&url';

export interface PitchSample {
  /** Detected fundamental in Hz, or null when there is no confident pitch. */
  pitchHz: number | null;
  clarity: number;
  /** AudioContext time of the frame. */
  t: number;
}

export interface StartOptions {
  /** Mode-2: comb-notch frequencies (Sa/Pa/Ma + harmonics) to filter out an external drone. */
  notchFrequencies?: number[];
  /** Linear input gain applied before detection (boosts quiet/child voices; preserves pitch). */
  inputGain?: number;
  /** Capture the mic to a Blob for on-device playback (never uploaded). */
  record?: boolean;
}

/**
 * Dev-only test hook for the Playwright screenshot harness (scripts/screenshots/).
 * Never set outside that harness — `window.__SWARASENSE_TEST__` has no production
 * setter anywhere in the shipped app, so this branch is dead code in normal use.
 * When present, `AudioEngine.start` replays `window.__SWARASENSE_TEST_PITCH_SERIES__`
 * (or whatever is later passed to `window.__feedPitch`) on a timer instead of opening
 * the mic / AudioWorklet, so the UI can be screenshotted without real audio hardware.
 */
declare global {
  interface Window {
    __SWARASENSE_TEST__?: boolean;
    __SWARASENSE_TEST_PITCH_SERIES__?: PitchSample[];
    __feedPitch?: (series: PitchSample[]) => void;
  }
}

/**
 * Owns the mic → (optional comb-notch) → AudioWorklet → pitch-callback pipeline
 * (ADR-0002/0003). Browser voice processing is disabled (AC-4.5). For Mode 2, an
 * external drone is attenuated by a comb-notch chain, and drone-only frames are
 * suppressed by a calibration-based presence gate (AC-7.3). Start from a user gesture.
 */
export class AudioEngine {
  private ctx: AudioContext | undefined;
  private stream: MediaStream | undefined;
  private node: AudioWorkletNode | undefined;
  private source: MediaStreamAudioSourceNode | undefined;
  private gateAnalyser: AnalyserNode | undefined;
  private gateBuf: Float32Array<ArrayBuffer> | undefined;
  private droneTemplate: Float32Array | undefined;
  private inputGainNode: GainNode | undefined;
  private inputGainValue = 1;
  private recorder: MediaRecorder | undefined;
  private recordedChunks: Blob[] = [];
  private recordingMime = '';
  // Screenshot-harness test mode (see the `declare global` block above) — unused
  // (always false) unless `window.__SWARASENSE_TEST__` is set.
  private testActive = false;
  private testTimer: ReturnType<typeof setInterval> | undefined;

  get running(): boolean {
    return this.ctx !== undefined || this.testActive;
  }

  /** Boost the detection signal (linear; preserves pitch). Applies live while running. */
  setInputGain(g: number): void {
    this.inputGainValue = g;
    if (this.inputGainNode) this.inputGainNode.gain.value = g;
  }

  async start(
    profile: InstrumentProfile,
    onPitch: (sample: PitchSample) => void,
    options: StartOptions = {},
  ): Promise<void> {
    if (this.running) return;

    // --- Test-only hook (screenshot harness) --------------------------------
    // Guarded by the flag above; production builds never set it, so real usage
    // always falls through to the real getUserMedia/AudioWorklet path below.
    if (typeof window !== 'undefined' && window.__SWARASENSE_TEST__) {
      this.startTestMode(onPitch);
      return;
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      video: false,
    });

    if (options.record) this.startRecording();

    const ctx = new AudioContext();
    await ctx.resume();
    await ctx.audioWorklet.addModule(pitchProcessorUrl);

    const source = ctx.createMediaStreamSource(this.stream);

    // Presence-gate tap on the raw signal (used only after drone calibration).
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    this.gateAnalyser = analyser;
    this.gateBuf = new Float32Array(analyser.fftSize);

    // Input gain boost (before detection; the raw signal still feeds the gate above).
    if (options.inputGain !== undefined) this.inputGainValue = options.inputGain;
    const gain = ctx.createGain();
    gain.gain.value = this.inputGainValue;
    source.connect(gain);
    this.inputGainNode = gain;

    // Optional Mode-2 comb-notch chain between the gain and the detector.
    let head: AudioNode = gain;
    for (const f of options.notchFrequencies ?? []) {
      const notch = ctx.createBiquadFilter();
      notch.type = 'notch';
      notch.frequency.value = f;
      notch.Q.value = 20;
      head.connect(notch);
      head = notch;
    }

    const node = new AudioWorkletNode(ctx, 'pitch-processor', {
      processorOptions: {
        rmsThreshold: profile.mpm.rmsThreshold,
        clarityThreshold: profile.mpm.clarityThreshold,
        minHz: profile.pitchRange.minHz,
        maxHz: profile.pitchRange.maxHz,
      },
    });
    node.port.onmessage = (e: MessageEvent) => {
      const sample = e.data as PitchSample;
      // Presence gate: if calibrated and the current frame is drone-only, report no input.
      if (sample.pitchHz !== null && this.droneTemplate && this.gateAnalyser && this.gateBuf) {
        this.gateAnalyser.getFloatTimeDomainData(this.gateBuf);
        if (!isVoicePresent(this.gateBuf, this.droneTemplate)) {
          onPitch({ pitchHz: null, clarity: 0, t: sample.t });
          return;
        }
      }
      onPitch(sample);
    };

    const mute = ctx.createGain();
    mute.gain.value = 0;
    head.connect(node);
    node.connect(mute);
    mute.connect(ctx.destination);

    this.ctx = ctx;
    this.node = node;
    this.source = source;
  }

  /**
   * Test-only: replay a synthetic `PitchSample[]` to `onPitch` on a timer instead of
   * opening the mic. Never called in production — gated by `window.__SWARASENSE_TEST__`
   * in `start()` above. The series is read from `window.__SWARASENSE_TEST_PITCH_SERIES__`
   * (set before `start()`, e.g. by scripts/screenshots/mock-mic.js) and loops so a
   * screenshot taken any time after start always lands on a live-looking frame.
   * `window.__feedPitch` is (re)installed here so the harness can swap in a new
   * series — e.g. to advance to a different phrase — while this session is running.
   */
  private startTestMode(onPitch: (sample: PitchSample) => void): void {
    this.testActive = true;
    let series = window.__SWARASENSE_TEST_PITCH_SERIES__ ?? [];
    let i = 0;
    window.__feedPitch = (newSeries: PitchSample[]) => {
      series = newSeries;
      i = 0;
    };
    const stepMs = 40; // ~25fps, close to the real worklet's callback cadence
    this.testTimer = setInterval(() => {
      if (series.length === 0) return;
      onPitch(series[i % series.length]!);
      i++;
    }, stepMs);
  }

  /** Capture a drone-only spectral template (AC-7.1/7.5). Call while only the drone sounds. */
  async calibrateDrone(durationMs = 2500): Promise<Float32Array> {
    const analyser = this.gateAnalyser;
    if (!analyser) throw new Error('calibrateDrone: start() the engine first');
    const frames: Float32Array[] = [];
    const intervalMs = 60; // > 1 pluck cycle worth of frames (AC-7.5)
    const iters = Math.max(1, Math.round(durationMs / intervalMs));
    for (let i = 0; i < iters; i++) {
      const buf = new Float32Array(analyser.fftSize);
      analyser.getFloatTimeDomainData(buf);
      frames.push(buf);
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    this.droneTemplate = buildDroneTemplate(frames);
    return this.droneTemplate;
  }

  clearDroneTemplate(): void {
    this.droneTemplate = undefined;
  }

  /** Begin capturing the mic stream to memory. Fail-soft: no-op if unsupported. */
  private startRecording(): void {
    if (typeof MediaRecorder === 'undefined' || !this.stream) return;
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    const mime = candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? '';
    try {
      const rec = mime
        ? new MediaRecorder(this.stream, { mimeType: mime })
        : new MediaRecorder(this.stream);
      this.recordedChunks = [];
      this.recordingMime = rec.mimeType || mime || 'audio/webm';
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) this.recordedChunks.push(e.data);
      };
      rec.start();
      this.recorder = rec;
    } catch {
      this.recorder = undefined;
    }
  }

  /** Stop the recorder and wait for its final chunk before returning. */
  private stopRecording(): Promise<void> {
    const rec = this.recorder;
    this.recorder = undefined;
    if (!rec || rec.state === 'inactive') return Promise.resolve();
    return new Promise<void>((resolve) => {
      rec.addEventListener('stop', () => resolve(), { once: true });
      rec.stop();
    });
  }

  /** Take ownership of the finalized recording (clears the internal buffer). */
  takeRecording(): { blob: Blob; mimeType: string } | null {
    if (this.recordedChunks.length === 0) return null;
    const mimeType = this.recordingMime || 'audio/webm';
    const blob = new Blob(this.recordedChunks, { type: mimeType });
    this.recordedChunks = [];
    this.recordingMime = '';
    return { blob, mimeType };
  }

  async stop(): Promise<void> {
    if (this.testActive) {
      this.testActive = false;
      clearInterval(this.testTimer);
      this.testTimer = undefined;
      return;
    }

    await this.stopRecording();

    this.node?.disconnect();
    this.source?.disconnect();
    this.gateAnalyser?.disconnect();
    this.inputGainNode?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    await this.ctx?.close();
    this.ctx = undefined;
    this.node = undefined;
    this.source = undefined;
    this.gateAnalyser = undefined;
    this.gateBuf = undefined;
    this.droneTemplate = undefined;
    this.inputGainNode = undefined;
  }
}
